'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import CustomSelect from '@/components/ui/CustomSelect';
import { motion, AnimatePresence } from 'framer-motion';

interface ConsultationModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface FormData {
    name: string;
    email: string;
    mobile: string;
    company: string;
    domain: string;
    service: string;
    budget: string;
    overview: string;
}

interface FormErrors {
    [key: string]: string;
}

const ConsultationModal = ({ isOpen, onClose }: ConsultationModalProps) => {
    const [isHydrated, setIsHydrated] = useState(false);
    const [formData, setFormData] = useState<FormData>({
        name: '',
        email: '',
        mobile: '',
        company: '',
        domain: '',
        service: 'Free Brand Audit (Value ₹5,000)',
        budget: '',
        overview: ''
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        setIsHydrated(true);
    }, []);

    useEffect(() => {
        if (!isHydrated) return;
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, isHydrated]);

    if (!isHydrated) return null;

    const serviceOptions = [
        'Free Brand Audit (Value ₹5,000)',
        'Google Business Profile Audit',
        'Website Performance Check',
        'SEO & Visibility Analysis',
        'Complete Brand Overhaul',
        'Website Development',
        'Mobile App Development',
        'Brand Visibility (SEO/GBP)',
        'E-commerce Development',
        'UI/UX Design'
    ];

    const budgetOptions = [
        '₹30,000 - ₹50,000',
        '₹50,000 - ₹1,00,000',
        '₹1,00,000 - ₹2,50,000',
        '₹2,50,000 - ₹5,00,000',
        '₹5,00,000+',
        'Lets discuss in connect'
    ];

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!formData.mobile.trim()) {
            newErrors.mobile = 'Mobile number is required';
        } else if (!/^[+]?[0-9]{10,15}$/.test(formData.mobile.replace(/\s/g, ''))) {
            newErrors.mobile = 'Please enter a valid mobile number';
        }
        if (!formData.service) newErrors.service = 'Please select a service';
        if (!formData.budget) newErrors.budget = 'Please select a budget range';
        if (!formData.overview.trim()) {
            newErrors.overview = 'Project overview is required';
        } else if (formData.overview.trim().length < 20) {
            newErrors.overview = 'Please provide at least 20 characters';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const response = await fetch('https://sitemapgenius.com/backend/contact.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    mobile: formData.mobile,
                    company: formData.company,
                    website: formData.domain,
                    service: formData.service,
                    budget: formData.budget,
                    message: formData.overview
                }),
            });

            const result = await response.json();

            if (result.success) {
                setSubmitSuccess(true);
                setTimeout(() => {
                    setSubmitSuccess(false);
                    onClose();
                    setFormData({
                        name: '',
                        email: '',
                        mobile: '',
                        company: '',
                        domain: '',
                        service: 'Free Brand Audit (Value ₹5,000)',
                        budget: '',
                        overview: ''
                    });
                }, 3000);
            } else {
                console.error('Submission failed:', result.message);
                // Optionally handle error UI here
            }
        } catch (error) {
            console.error('Submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
                    />

                    {/* Creative Full Screen / Large Sheet Overlay */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="relative w-full h-full sm:h-[95vh] sm:w-[95vw] sm:max-w-6xl sm:rounded-t-3xl sm:rounded-b-none md:rounded-3xl bg-white shadow-2xl overflow-hidden flex flex-col md:flex-row"
                    >
                        {/* Close Button */}
                        <button
                            onClick={onClose}
                            className="absolute top-6 right-6 z-20 p-2 bg-white/50 backdrop-blur-sm rounded-full hover:bg-slate-100 transition-colors group"
                        >
                            <Icon name="XMarkIcon" size={28} className="text-slate-500 group-hover:text-slate-900 transition-colors" />
                        </button>

                        {/* Left Side: Creative Visual / Pitch */}
                        <div className="hidden md:flex md:w-1/3 bg-slate-900 text-white p-12 flex-col justify-between relative overflow-hidden">
                            {/* Abstract Decorative Elements */}
                            <div className="absolute top-0 left-0 w-full h-full opacity-20">
                                <div className="absolute top-10 left-10 w-64 h-64 bg-indigo-500 rounded-full blur-3xl"></div>
                                <div className="absolute bottom-10 right-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
                            </div>

                            <div className="relative z-10">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-bold uppercase tracking-wider mb-8">
                                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                                    Free Audit worth ₹5,000
                                </div>
                                <h2 className="text-4xl lg:text-5xl font-bold leading-tight mb-6">
                                    Unlock Your <br />
                                    <span className="text-indigo-400">Brand's Potential</span>
                                </h2>
                                <p className="text-slate-300 text-lg leading-relaxed">
                                    Let our experts dive deep into your digital presence. We'll identify hidden opportunities and provide a roadmap to dominate your market.
                                </p>
                            </div>

                            <div className="relative z-10 space-y-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                                        <Icon name="ChartBarIcon" size={24} className="text-indigo-400" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-lg">In-Depth Analysis</div>
                                        <div className="text-sm text-slate-400">No automated fluff. Real insights.</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                                        <Icon name="RocketLaunchIcon" size={24} className="text-indigo-400" />
                                    </div>
                                    <div>
                                        <div className="font-bold text-lg">Actionable Strategy</div>
                                        <div className="text-sm text-slate-400">Clear steps to boost ROI.</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side: The Form */}
                        <div className="flex-1 overflow-y-auto bg-white p-8 md:p-12 relative">
                            {/* Mobile Header (Visible only on small screens) */}
                            <div className="md:hidden mb-8 pr-12">
                                <h2 className="text-3xl font-bold text-slate-900 mb-2">Claim Free Audit</h2>
                                <p className="text-slate-500">Get a comprehensive analysis worth ₹5,000.</p>
                            </div>

                            {submitSuccess ? (
                                <div className="flex flex-col items-center justify-center h-full text-center py-12">
                                    <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mb-6">
                                        <Icon name="CheckCircleIcon" size={48} className="text-green-500" />
                                    </div>
                                    <h3 className="text-3xl font-bold text-slate-900 mb-4">Request Received!</h3>
                                    <p className="text-lg text-slate-600 max-w-md mx-auto mb-8">
                                        We've received your audit request. Our team will start analyzing your brand and get back to you within 24 hours.
                                    </p>
                                    <button
                                        onClick={onClose}
                                        className="px-8 py-3 bg-slate-900 text-white rounded-lg font-bold hover:bg-slate-800 transition-colors"
                                    >
                                        Close Window
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                Full Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${errors.name ? 'border-red-500' : 'border-slate-300'}`}
                                                placeholder="Abdul Sohail"
                                            />
                                            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                Email Address <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
                                                placeholder="abdul@sitemapgenius.com"
                                            />
                                            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                Mobile Number <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="tel"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${errors.mobile ? 'border-red-500' : 'border-slate-300'}`}
                                                placeholder="+91 8686284396"
                                            />
                                            {errors.mobile && <p className="mt-1 text-sm text-red-500">{errors.mobile}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                                                Company Name
                                            </label>
                                            <input
                                                type="text"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                                placeholder="Your Company"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Website / Domain
                                        </label>
                                        <input
                                            type="url"
                                            name="domain"
                                            value={formData.domain}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                            placeholder="https://yourwebsite.com"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <CustomSelect
                                                label="Service Interest"
                                                required
                                                options={serviceOptions}
                                                value={formData.service}
                                                onChange={(value) => {
                                                    setFormData(prev => ({ ...prev, service: value }));
                                                    if (errors.service) setErrors(prev => ({ ...prev, service: '' }));
                                                }}
                                                error={errors.service}
                                                placeholder="Select Service"
                                            />
                                        </div>
                                        <div>
                                            <CustomSelect
                                                label="Estimated Budget"
                                                required
                                                options={budgetOptions}
                                                value={formData.budget}
                                                onChange={(value) => {
                                                    setFormData(prev => ({ ...prev, budget: value }));
                                                    if (errors.budget) setErrors(prev => ({ ...prev, budget: '' }));
                                                }}
                                                error={errors.budget}
                                                placeholder="Select Range"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Project Overview <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            name="overview"
                                            value={formData.overview}
                                            onChange={handleChange}
                                            rows={4}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none ${errors.overview ? 'border-red-500' : 'border-slate-300'}`}
                                            placeholder="Tell us about your goals and what you're hoping to achieve..."
                                        />
                                        {errors.overview && <p className="mt-1 text-sm text-red-500">{errors.overview}</p>}
                                    </div>

                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-indigo-600 text-white font-bold py-4 px-6 rounded-lg hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-500/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-0.5"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    <Icon name="SparklesIcon" size={20} />
                                                    Claim Free Audit Now
                                                </>
                                            )}
                                        </button>
                                        <p className="text-center text-xs text-slate-400 mt-4">
                                            Limited time offer. Valid for businesses with active websites or GMB profiles.
                                        </p>
                                    </div>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default ConsultationModal;